import Chip from 'tio-reimagined/components/chip';
import { dropTask } from 'ember-concurrency';
import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import { divide } from 'tio-ui/math';
import EmployeeEligibility from 'tio-common/components/tas/employee-eligibility';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import Table from 'tio-reimagined/components/table';
import type EmployeeModel from 'tio-common/models/employee';
import type IntlService from 'ember-intl/services/intl';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasApplicationModel from 'tio-common/models/tas-application';
import type TasEligibilityModel from 'tio-common/models/tas-eligibility';
import type TasProgramInstanceModel from 'tio-common/models/tas-program-instance';

const number = Number;

export interface TuitionAssistanceProfileTuitionAssistanceSignature {
  Args: {
    employee: EmployeeModel;
  };
  Element: HTMLElement;
}

export default class TuitionAssistanceProfileTuitionAssistanceComponent extends Component<TuitionAssistanceProfileTuitionAssistanceSignature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  @tracked holdParticipantModal = false;
  @tracked isCollapsed = true;

  constructor(owner: Owner, args: TuitionAssistanceProfileTuitionAssistanceSignature['Args']) {
    super(owner, args);

    // Check if we should auto-expand and scroll to eligibility
    const queryParams = this.router.currentRoute?.queryParams;
    if (queryParams?.scrollToEligibility === 'true') {
      this.isCollapsed = false;

      // Scroll to participation status after section expands
      setTimeout(() => {
        const element = document.getElementById('participation-status');
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }

  get employee() {
    return this.args.employee;
  }

  routeForModelName(modelName: string) {
    if (modelName === 'tas-program-instance') {
      return 'authenticated.admin.tuition-assistance.program-instances.show';
    }
    if (modelName === 'tas-application') {
      return 'authenticated.admin.tuition-assistance.applications.show';
    }
    return '';
  }

  labelForModelName(modelName: string) {
    if (modelName === 'tas-program-instance') {
      return 'Program Application';
    }
    if (modelName === 'tas-application') {
      return 'Courses Application';
    }
    return '';
  }

  tasProgramInstancesData = trackedFunction(this, async () => {
    if (!this.employee) {
      return [];
    }

    const instances = await this.store.query('tas-program-instance', {
      filter: { employee: this.employee.id },
      include: 'tas-program-template,tas-applications.tas-courses,tas-applications.tas-assistances',
    });

    return instances;
  });

  get isLoading() {
    return this.tasProgramInstancesData.isLoading;
  }

  get tasProgramInstances() {
    return this.tasProgramInstancesData.value || [];
  }

  tasApplications = (instance?: TasProgramInstanceModel) => {
    return instance?.tasApplications.slice() || [];
  };

  tasCourseNames = (application?: TasApplicationModel) => {
    const courses = application?.activeCourses.slice() || [];
    const courseNames = courses.map((course) => {
      return course.displayName;
    });

    return courseNames.join(', ');
  };

  statusText = (state: string): string => {
    if (this.intl.exists(`common.status_message.to.${state}`)) {
      return this.intl.t(`common.status_message.to.${state}`);
    }

    return this.intl.t('common.status_message.to.TAS.ProgramInstanceState.UPDATED_ON');
  };

  get records() {
    return [...this.tasProgramInstances] as TasProgramInstanceModel[];
  }

  get employeeEligibilities() {
    return this.args.employee.tasEligibilities as TasEligibilityModel[];
  }

  get isInActive() {
    return this.employee?.tasParticipant?.state === 'TAS.ParticipantState.INACTIVE';
  }

  get holdStatus() {
    return this.tasParticipationStatus === 'ACTIVE';
  }

  get tasParticipationStatus() {
    return this.employee?.tasParticipant?.state.split('.')[2];
  }

  toggleHoldParticipantModal = () => {
    this.holdParticipantModal = !this.holdParticipantModal;
  };

  get participantId() {
    return this.employee.tasParticipant?.id;
  }

  get holdChangeNote() {
    return `Hold/Reactivate employee: ${this.employee.fullName}`;
  }

  toggleHold = dropTask(async () => {
    if (this.tasParticipationStatus === 'ACTIVE') {
      await this.store
        .adapterFor('tas-participant')
        // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
        .holdTasParticipant(this.participantId, { changelog: this.holdChangeNote });
    } else {
      await this.store
        .adapterFor('tas-participant')
        // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
        .removeTasParticipantHold(this.participantId, { changelog: this.holdChangeNote });
    }
    await this.employee.tasParticipant.reload();
    this.toggleHoldParticipantModal();
  });

  <template>
    <Section @collapsed={{this.isCollapsed}}>
      <:header>{{t "tuition_assistance.default"}}</:header>
      <:body>
        <VStack @collapsed={{true}}>
          <Section>
            <Table @isLoading={{this.isLoading}}>
              <:thead as |options|>
                <options.tr>
                  <options.th>{{t "common.id"}}</options.th>
                  <options.th>{{t "tuition_assistance.application_name"}}</options.th>
                  <options.th>
                    {{t "tuition_assistance.program_details.application_history.state"}}
                  </options.th>
                  <options.th>
                    {{t "tuition_assistance.program_details.status.paid_date"}}
                  </options.th>
                  <options.th>
                    {{t "tuition_assistance.program_details.status.paid_total"}}
                  </options.th>
                  <options.th>
                    {{t "tuition_assistance.program_details.status.approved_total"}}
                  </options.th>
                </options.tr>
              </:thead>
              <:tbody as |options|>
                {{#each this.records as |record|}}
                  <options.tr class="align-top">
                    <options.td class="text-ocean-600 underline">
                      <LinkTo
                        @route={{this.routeForModelName record.modelName}}
                        @model={{record.id}}
                      >
                        {{record.id}}
                      </LinkTo>
                    </options.td>
                    <options.td>{{record.adminDisplayName}}</options.td>
                    <options.td>{{this.statusText record.state}}</options.td>
                    <options.td>-</options.td>
                    <options.td>-</options.td>
                    <options.td>-</options.td>
                  </options.tr>
                  {{#each (this.tasApplications record) as |application|}}
                    <options.tr class="bg-tio-gray-100">
                      <options.td class="pl-9 pr-3 text-ocean-600">
                        <LinkTo
                          @route={{this.routeForModelName application.modelName}}
                          @model={{application.id}}
                          class="underline"
                        >
                          {{application.id}}
                        </LinkTo>
                        {{#if application.isRefundRequested}}
                          <Chip @size="sm" @intent="danger" class="ml-2 text-nowrap">
                            {{t "tuition_assistance.application_details.in_repayment"}}
                          </Chip>
                        {{/if}}
                      </options.td>
                      <options.td>
                        {{application.displayName}}
                        {{#if (this.tasCourseNames application)}}
                          ({{this.tasCourseNames application}})
                        {{/if}}
                      </options.td>
                      <options.td>{{this.statusText application.state}}</options.td>
                      {{#if application.isPaid}}
                        <options.td>
                          {{safeFormatDate (if application.paidDate application.paidDate "")}}
                        </options.td>
                      {{else}}
                        <options.td>-</options.td>
                      {{/if}}
                      {{#if application.isPaid}}
                        <options.td>
                          {{safeFormatNumber
                            (divide application.paidTotal 100)
                            style="currency"
                            currency="USD"
                          }}
                        </options.td>
                      {{else}}
                        <options.td>-</options.td>
                      {{/if}}
                      {{#if application.isApproved}}
                        <options.td>
                          {{safeFormatNumber
                            (divide (number application.approvedTotal) 100)
                            style="currency"
                            currency="USD"
                          }}
                        </options.td>
                      {{else}}
                        <options.td>-</options.td>
                      {{/if}}
                    </options.tr>
                  {{/each}}
                {{else}}
                  <options.tr>
                    <options.td colspan="5">
                      {{t "common.no_records_found"}}
                    </options.td>
                  </options.tr>
                {{/each}}
              </:tbody>
            </Table>
          </Section>
          <EmployeeEligibility @employee={{@employee}} />
        </VStack>
      </:body>
    </Section>
  </template>
}
